/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    arrayOf,
    bool,
    func,
    number,
    oneOfType,
    shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Pill from '../../../ProductFacet/Pill';
import ProductCount from '../../../ProductFacet/ProductCount';
import SelectedProductFacets from '../../../ProductFacet/SelectedProductFacets';
import useCheckedFacets from '../../../../../helpers/hooks/useCheckedFacets';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const SORT_BY_OPTIONS = [
    { label: 'Price: Low to High', value: 'PRICE_ASC' },
    { label: 'Price: High to Low', value: 'PRICE_DESC' },
    { label: 'Best Sellers', value: 'BEST_SELLERS' },
];

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    selectedRow: {
        padding: '16px 0',
    },
    divider: {
        borderTop: '1px solid #E5E5E5',
    },
    footerRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '14px 0',
        gap: '12px',
    },
    searchFooterRow: {
        padding: '14px 0 0 !important',
    },
    productCountWrap: {
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        '& > div': {
            padding: 0,
        },
    },
    sortTrigger: {
        border: 'none',
        padding: 0,
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'inherit',
        '& > div': {
            padding: '8px 12px',
        },
    },
    sortDropdownContainer: {
        marginLeft: 'auto',
        position: 'relative',
    },
    sortDropdown: {
        position: 'absolute',
        right: 0,
        top: 'calc(100% + 8px)',
        zIndex: 3,
        background: '#FFFFFF',
        border: '1px solid #E5E5E5',
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        minWidth: '220px',
    },
    options: {
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    optionButton: {
        width: '100%',
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        padding: '12px 16px',
        fontSize: '14px',
        color: '#000000',
        cursor: 'pointer',
        '&:hover': {
            background: '#F5F5F5',
        },
    },
    selectedOptionButton: {
        background: '#F5F5F5',
        fontWeight: 600,
    },
}));

const SortByDesktop = ({
    options,
    selectedValue,
    onApply,
    loading,
}) => {
    const classes = useStyles();
    const containerRef = React.useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };
    }, []);

    return (
        <div ref={containerRef} className={classes.sortDropdownContainer}>
            <button
                type="button"
                className={classes.sortTrigger}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setIsOpen((prev) => !prev)}
                disabled={loading}
            >
                <Pill
                    label="Sort"
                    endIconType="sortby"
                    variant={selectedValue === '' ? 'inactive' : 'active'}
                    disabled={loading}
                />
            </button>
            {isOpen && (
                <div className={classes.sortDropdown}>
                    <ul className={classes.options}>
                        {options.map((option) => (
                            <li key={option.value}>
                                <button
                                    type="button"
                                    className={`${classes.optionButton} ${selectedValue === option.value ? classes.selectedOptionButton : ''}`}
                                    onClick={() => {
                                        onApply(option.value);
                                        handleClose();
                                    }}
                                >
                                    {option.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

SortByDesktop.propTypes = {
    options: arrayOf(shape({
        label: string.isRequired,
        value: string.isRequired,
    })).isRequired,
    selectedValue: string,
    onApply: func.isRequired,
    loading: bool,
};

SortByDesktop.defaultProps = {
    selectedValue: null,
    loading: false,
};

const ProductDesktopFiltersV4 = ({
    productCount,
    productFacets,
    loading,
    setFacetsDataInCollection,
    selectedSortValue,
    onSortChange,
    disableSort,
    searchPage,
    searchTerm,
}) => {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const checkedFacetsData = useCheckedFacets();
    const isAggregatorFacetErrorResolver = useSelector(getFeatureFlag('is-facet-throwing-error-resolver-enabled'));

    const searchValue = unescape(history.location?.search);

    const [selectedProductFacets, setSelectedProductFacets] = useState(checkedFacetsData);

    React.useEffect(() => {
        setSelectedProductFacets(checkedFacetsData);
        setFacetsDataInCollection(checkedFacetsData);
    }, [searchValue]);

    const selectedFacets = React.useMemo(() => (
        selectedProductFacets
            .map((value) => {
                for (let i = 0; i < productFacets.length; i += 1) {
                    const facet = productFacets[i];
                    const entry = facet.entries.find((e) => e.value === value);
                    if (entry) {
                        return { label: entry.name, value: entry.value };
                    }
                }
                return null;
            })
            .filter((item) => item !== null)
    ), [selectedProductFacets, productFacets]);

    const selectedSortFacet = React.useMemo(() => {
        if (disableSort) return null;
        if (!selectedSortValue) return null;
        const selectedSort = SORT_BY_OPTIONS.find((option) => option.value === selectedSortValue);
        return {
            label: selectedSort?.label || selectedSortValue,
            value: selectedSortValue,
        };
    }, [disableSort, selectedSortValue]);

    const timerId = React.useRef(null);

    const yieldToMain = () => {
        // Use scheduler.yield if it exists:
        if (typeof window !== 'undefined' && 'scheduler' in window && 'yield' in window.scheduler) {
            return window.scheduler.yield();
        }

        // Fall back to setTimeout:
        return new Promise((resolve) => setTimeout(resolve, 0));
    };

    const historyReplace = async (searchParam) => {
        history.replace({
            search: searchParam,
            state: selectedProductFacets,
        });
        await yieldToMain();
    };

    React.useEffect(() => {
        let delimiter = '&';
        if (isAggregatorFacetErrorResolver) {
            delimiter = '&facet=';
        }
        const urlSelectedParams = selectedProductFacets.join(delimiter);
        clearTimeout(timerId.current);
        timerId.current = setTimeout(() => { // delay the history for two ms
            let searchParam = '';
            if (urlSelectedParams) {
                searchParam = `facet=${urlSelectedParams}`;
            } else if (searchValue && !searchValue.includes('facet')) {
                searchParam = searchValue;
            }
            historyReplace(searchParam); // for scheduler yield
        }, 50);
    }, [selectedProductFacets?.length]);

    const handleRemove = (value) => {
        setSelectedProductFacets((prev) => prev.filter((v) => v !== value));
    };

    const handleRemoveSort = () => {
        if (disableSort) return;
        onSortChange('');
    };

    const handleClearAll = () => {
        setSelectedProductFacets([]);
        if (!disableSort) {
            onSortChange('');
        }
        setFacetsDataInCollection([]);
    };

    const hasSelectedValues = selectedFacets.length > 0 || !!selectedSortFacet;

    const handleSortChange = (value) => {
        onSortChange(value);
        dispatch(emitCustomTrackingEvent({
            eventType: 'custom',
            eventSubType: 'feature-tracking',
            action: 'Click',
            category: searchPage ? 'Search Page' : 'Collection Page',
            label: 'Apply Filter',
            tag: `orderBy | ${value}`,
            productFilters: { orderBy: value },
        }));
    };

    return (
        <div className={classes.root} data-product-desktop-filters-v4="true">
            {hasSelectedValues && (
                <div className={classes.selectedRow}>
                    <SelectedProductFacets
                        selectedFacets={selectedFacets}
                        selectedSortFacet={selectedSortFacet}
                        onRemove={handleRemove}
                        onRemoveSort={handleRemoveSort}
                        onClearAll={handleClearAll}
                        loading={loading}
                        isDesktop
                    />
                </div>
            )}
            {(!searchPage || hasSelectedValues) && <div className={classes.divider} />}
            <div className={`${classes.footerRow}${searchPage ? ` ${classes.searchFooterRow}` : ''}`}>
                <div className={classes.productCountWrap}>
                    <ProductCount productCount={productCount} searchTerm={searchTerm} />
                </div>
                {!disableSort && (
                    <SortByDesktop
                        options={SORT_BY_OPTIONS}
                        selectedValue={selectedSortValue}
                        onApply={handleSortChange}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    );
};

ProductDesktopFiltersV4.propTypes = {
    productCount: oneOfType([number, string]),
    productFacets: arrayOf(shape({
        name: string.isRequired,
        displayOpen: bool,
        entries: arrayOf(shape({
            name: string.isRequired,
            value: string.isRequired,
            count: number,
            from: number,
            to: number,
            tooltip: string,
        })),
    })),
    loading: bool,
    setFacetsDataInCollection: func,
    selectedSortValue: string,
    onSortChange: func,
    disableSort: bool,
    searchPage: bool,
    searchTerm: string,
};

ProductDesktopFiltersV4.defaultProps = {
    productCount: '',
    productFacets: [],
    loading: false,
    setFacetsDataInCollection: () => {},
    selectedSortValue: '',
    onSortChange: () => {},
    disableSort: false,
    searchPage: false,
    searchTerm: '',
};

export default ProductDesktopFiltersV4;
